import type { ReactElement } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { formatMoney, t } from '@aquabox/shared';
import type { MessageKey } from '@aquabox/shared';
import { Kpi } from '../../components/Kpi';
import type { Dashboard } from '../../api/types';

type Source = 'ABANT_PANEL' | 'HAYAT_PANEL' | 'BIZIM';

const SOURCE_LABEL: Record<Source, MessageKey> = {
  ABANT_PANEL: 'panelRep.sourceAbant',
  HAYAT_PANEL: 'panelRep.sourceHayat',
  BIZIM: 'panelRep.sourceBizim',
};

/**
 * Tek bir kaynak satırı: "Abant 65" gibi, yalnız sayı sıfır değilse.
 * Kanal kartının altındaki ince satırda kullanılır; kendi başına KPI değil.
 */
function SourceBreakdownRow({
  source,
  count,
}: {
  source: Source;
  count: number;
}): ReactElement | null {
  if (count <= 0) return null;
  return (
    <span style={{ marginRight: 8 }}>
      <span style={{ color: 'var(--text-secondary)' }}>{t(SOURCE_LABEL[source])}</span>{' '}
      <span style={{ fontWeight: 600 }}>{count}</span>
    </span>
  );
}

/** Kaynak kırılımını `ABANT_PANEL, HAYAT_PANEL, BIZIM` sabit sırasında dizer. */
function SourceBreakdown({
  rows,
}: {
  rows: { source: Source; count: number }[];
}): ReactElement | null {
  const order: Source[] = ['ABANT_PANEL', 'HAYAT_PANEL', 'BIZIM'];
  const visible = order
    .map((s) => rows.find((r) => r.source === s))
    .filter((r): r is { source: Source; count: number } => Boolean(r) && r!.count > 0);
  if (visible.length === 0) return null;
  return (
    <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
      {visible.map((r) => (
        <SourceBreakdownRow key={r.source} source={r.source} count={r.count} />
      ))}
    </span>
  );
}

/**
 * "Bugünkü ciro" altı: "Abant ₺9.186 · Hayat ₺1.080" gibi kaynak başına teslim cirosu.
 *
 * Ciro YALNIZ teslim edilenlerden — iptal, başarısız, yolda, bekleyen sıfır. Float toplama
 * yapılmaz; sunucu Decimal ile döner (kural 4), panel yalnız biçimler.
 */
function RevenueBySource({
  rows,
}: {
  rows: { source: Source; revenue: string }[];
}): ReactElement | null {
  const order: Source[] = ['ABANT_PANEL', 'HAYAT_PANEL', 'BIZIM'];
  const visible = order
    .map((s) => rows.find((r) => r.source === s))
    .filter((r): r is { source: Source; revenue: string } => Boolean(r) && Number(r?.revenue) > 0);
  if (visible.length === 0) return null;
  return (
    <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
      {visible.map((r, i) => (
        <span key={r.source} style={{ marginLeft: i === 0 ? 0 : 8 }}>
          {t(SOURCE_LABEL[r.source])} {formatMoney(r.revenue, false)}
        </span>
      ))}
    </span>
  );
}

/**
 * Genel Bakış'ın üst dört kartı (bugün). Her kart bir sayfaya gider:
 *   1) Ciro bugün — `combinedRevenue.total` (sunucu toplamı, panel toplamaz)     → Raporlar
 *   2) Teslim / atanmış — `deliveredToday` / (`deliveredToday` + `assigned` + `onRoute`) → Siparişler
 *   3) Devreden — `carriedOver` (devir olaylı açık sipariş)                       → Siparişler
 *   4) Onay bekleyen — `approvalTotal` (mobil `approvalCount` ile aynı)            → Depo (yeniden yükleme onayı)
 *
 * `today` yoksa (yükleme ya da hata) dört kartın değeri "–" olur; sahte 0 gösterilmez.
 * Kart 2'nin payı ile paydası AYNI teslim kümesinden gelir; `assigned`/`onRoute` anlıktır.
 */
export function KpiCards({ today }: { today: Dashboard['today'] | null | undefined }) {
  const navigate = useNavigate();
  const dash = t('dashboard.kpi.placeholder');
  const bySource = today?.bySource ?? [];
  const toplam = today ? today.deliveredToday + today.assigned + today.onRoute : 0;
  return (
    <section className="kpi-grid">
      <KpiWithHint hint={t('dashboard.kpi.revenueHint')}>
        <Kpi
          label={t('dashboard.kpi.revenue')}
          value={today ? formatMoney(today.combinedRevenue.total) : dash}
          onClick={() => void navigate('/raporlar')}
          note={
            today ? (
              <>
                <RevenueBySource rows={today.combinedRevenue.bySource} />
                <div style={{ marginTop: 4, fontSize: 11.5, color: 'var(--text-secondary)' }}>
                  {t('panel.revenueSplit', {
                    ours: formatMoney(today.combinedRevenue.ours, false),
                    panel: formatMoney(today.combinedRevenue.panel, false),
                  })}
                </div>
              </>
            ) : null
          }
        />
      </KpiWithHint>
      <KpiWithHint hint={t('dashboard.kpi.deliveredHint')}>
        <Kpi
          label={t('dashboard.kpi.deliveredOfAssigned')}
          value={
            today
              ? t('dashboard.kpi.deliveredRatio', {
                  delivered: today.deliveredToday,
                  total: toplam,
                })
              : dash
          }
          onClick={() => void navigate('/siparisler')}
          note={
            today ? (
              <>
                <SourceBreakdown
                  rows={bySource.map((s) => ({ source: s.source, count: s.delivered }))}
                />
                {today.cancelled > 0 ? (
                  <div
                    style={{
                      marginTop: 4,
                      fontSize: 11.5,
                      color: 'var(--text-secondary)',
                    }}
                  >
                    {t('panel.deliveredSubnote', { n: today.cancelled })}
                  </div>
                ) : null}
              </>
            ) : null
          }
        />
      </KpiWithHint>
      <KpiWithHint hint={t('dashboard.kpi.carriedOverHint')}>
        <Kpi
          label={t('dashboard.kpi.carriedOver')}
          value={today ? today.carriedOver : dash}
          accent={today && today.carriedOver > 0 ? 'warning' : undefined}
          onClick={() => void navigate('/siparisler')}
          note={
            <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
              {t('dashboard.kpi.carriedOverNote')}
            </span>
          }
        />
      </KpiWithHint>
      <KpiWithHint hint={t('dashboard.kpi.approvalsHint')}>
        <Kpi
          label={t('dashboard.kpi.approvals')}
          value={today ? today.approvalTotal : dash}
          accent={today && today.approvalTotal > 0 ? 'warning' : undefined}
          onClick={() => void navigate('/depo')}
        />
      </KpiWithHint>
    </section>
  );
}

/**
 * KPI kutusunun sağ üstüne küçük "i" ipucu ekler — hangi siparişleri saydığını tek cümleyle
 * söyler (`title` + `aria-label`, fare ile üstüne gelinince). `Kpi` bileşeni bu paketin dışında
 * (`components/Kpi.tsx`, dosya sahipliği listesinde YOK); ipucu bu yüzden `Kpi`'nin İÇİNE değil
 * ÜSTÜNE, konumlandırılmış bir kardeş öğe olarak eklenir.
 */
function KpiWithHint({ hint, children }: { hint: string; children: ReactElement }): ReactElement {
  return (
    <div style={{ position: 'relative' }}>
      {children}
      <span
        title={hint}
        aria-label={hint}
        role="img"
        style={{
          position: 'absolute',
          top: 14,
          right: 14,
          width: 16,
          height: 16,
          borderRadius: '50%',
          background: 'var(--border)',
          color: 'var(--text-secondary)',
          fontSize: 11,
          fontWeight: 700,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'help',
        }}
      >
        i
      </span>
    </div>
  );
}

/**
 * "Bölge tanımlı değil" tek satır uyarısı — kartın üstünde. Aktif bölge sayısı 0 ise
 * gösterilir; patron bölge eklemeden bizim siparişlerimiz araçlara otomatik düşmez.
 */
export function RegionMissingBanner({
  activeRegionCount,
}: {
  activeRegionCount: number;
}): ReactElement | null {
  if (activeRegionCount > 0) return null;
  return (
    <div
      style={{
        padding: '10px 14px',
        borderRadius: 'var(--r-card)',
        background: 'var(--bg-warning-soft)',
        borderLeft: '3px solid var(--warning)',
        fontSize: 13.5,
        display: 'flex',
        alignItems: 'center',
        gap: 8,
      }}
    >
      <span>{t('panel.regionsMissing')}</span>
      <Link
        to="/bolgeler"
        style={{
          color: 'var(--primary)',
          fontSize: 12.5,
          fontWeight: 600,
          marginLeft: 'auto',
        }}
      >
        {t('panel.regionsMissingLink')} →
      </Link>
    </div>
  );
}
